import { PDFViewer, pdf } from "@react-pdf/renderer";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Col, Row } from "reactstrap";

import "@/assets/scss/modern-modal.css";
import InfoRow from "@/components/Common/Inforow";
import TransactionDetailsModal from "@/components/Common/Modals/TransactionDetailsModal";
import Section from "@/components/Common/Section";
import TransactionFilesList from "@/components/Common/TransactionFilesList";
import HistoryList from "@/components/Common/HistoryList";
import useCustomHook from "@/components/Hooks/useCustomHook";
import { formatDate } from "@/helpers/date_helper";

import ActionModal from "./ActionModal";
import LoaPDf from "./LoaPDf";

import { useGetLOARequestLogsQuery } from "@/api/Endpoints/Globo/Transactions/LOARequest/LOARequestLogs";
import { useFindLOARequestsQuery, useGetFilesLOARequestsQuery, useUploadLOAFileRequestsMutation } from "@/api/Endpoints/Globo/Transactions/LOARequest/LOARequests";

const S3_BASE = process.env.REACT_APP_S3;

function TransactionDetailsTab({ data }) {
    return (
        <div style={{ paddingTop: 4 }}>
            <Row>
                <Col lg={6}>
                    <Section title="Request Details">
                        <Row>
                            <Col lg={6}>
                                <InfoRow icon="bx bx-user" label="Patient" value={data?.patientName} />
                            </Col>
                            <Col lg={6}>
                                <InfoRow icon="bx bx-user-check" label="Doctor" value={data?.doctorName} />
                            </Col>
                            <Col lg={6}>
                                <InfoRow icon="bx bx-cog" label="Program Name" value={data?.programName} />
                            </Col>
                            <Col lg={6}>
                                <InfoRow icon="bx bx-cog" label="Purchasing Account" value={data?.purchasingAccountName} />
                            </Col>
                            <Col lg={6}>
                                <InfoRow icon="bx bx-plus-medical" label="Product" value={data?.productName} />
                            </Col>
                            <Col lg={6}>
                                <InfoRow icon="bx bx-hash" label="Quantity" value={data?.quantity + " " + data?.uom} />
                            </Col>
                            <Col lg={6}>
                                <InfoRow icon="bx bx-calendar" label="Expiry Date" value={formatDate(data?.expiryDate)} />
                            </Col>
                            <Col lg={6}>
                                <InfoRow icon="bx bx-calendar" label="Infusion Date" value={formatDate(data?.infusionDate)} />
                            </Col>
                        </Row>
                    </Section>

                    <Section title="Remarks">
                        <Row>
                            <Col lg={12}>
                                <InfoRow icon="bx bx-message-square-detail" label="Remarks" value={data?.remarks} />
                            </Col>
                        </Row>
                    </Section>

                    <Section title="Status Details">
                        <Row>
                            <Col lg={6}>
                                <InfoRow label="Date Submitted" value={formatDate(data.dateSubmitted)} icon="ri-calendar-2-line" />
                            </Col>
                            <Col lg={6}>
                                <InfoRow label="Submitted By" value={data.submittedByName} icon="ri-user-3-line" />
                            </Col>
                            <Col lg={6}>
                                <InfoRow label="Date Approved" value={formatDate(data.dateApproved)} icon="ri-calendar-2-line" />
                            </Col>
                            <Col lg={6}>
                                <InfoRow label="Approved By" value={data.approvedByName} icon="ri-user-3-line" />
                            </Col>
                        </Row>
                    </Section>
                </Col>
                <Col lg={6}>
                    <div className="p-0" style={{ height: "calc(100vh - 150px)", overflow: "hidden" }}>
                        {data?.loaFilePath !== null ? (
                            <iframe
                                src={`${S3_BASE + data?.loaFilePath}#zoom=100`}
                                title={`Letter of Availment Report`}
                                width="100%"
                                height="100%"
                                style={{ border: "none" }}
                            />
                        ) : (
                            <PDFViewer style={{ width: "100%", height: "100%", border: "none" }}>
                                <LoaPDf pdfTitle={"Letter of Availment Report"} data={data} />
                            </PDFViewer>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
}

export default function ViewDetails({ show, onCloseClick, data, parentKey, onUpdateParentKey }) {
    if (!show) return null;

    const TABS = useMemo(
        () => [
            { key: "requestInfo", label: "Request Details", icon: "ri-user-line", component: TransactionDetailsTab },
            {
                key: "history", label: "History", icon: "ri-shield-flash-line", component: () =>
                    <HistoryList
                        transactionId={data?.loaRequestId}
                        useLogsQuery={useGetLOARequestLogsQuery}
                        idKey="loaRequestId"
                        title="History"
                    />
            },
            {
                key: "files", label: "Files", icon: "ri-folder-line", component: () =>
                    <TransactionFilesList
                        transactionId={data?.loaRequestId}
                        useFilesQuery={useGetFilesLOARequestsQuery}
                        idKey="loaRequestId"
                        title="Files"
                    />
            },
        ],
        [data?.loaRequestId],
    );

    const { state, customFunction } = useCustomHook({
        tabRefreshKey: 0,
    });

    const { data: transactionData, refetch } = useFindLOARequestsQuery(
        { loaRequestId: data?.loaRequestId },
        { skip: !data?.loaRequestId, refetchOnMountOrArgChange: show },
    );
    const [uploadLOAFile, { isLoading: isUploading }] = useUploadLOAFileRequestsMutation();

    const findData = transactionData?.returnData;

    const { reset, control } = useForm({ defaultValues: {} });

    useEffect(() => {
        reset({
            ...data,
            ...findData,
        });
        customFunction.updateState({
            data: {
                ...data,
                ...findData,
            },
        });
    }, [show, data, findData, refetch, reset]);

    const handleActions = async (bool, data) => {
        if (bool) {
            const pdfDoc = <LoaPDf pdfTitle={"LOA Request"} data={data} />;
            const blob = await pdf(pdfDoc).toBlob();
            const file = new File([blob], `LOA_${data?.referenceNo || "Request"}.pdf`, { type: "application/pdf" });
            const res = await uploadLOAFile({
                loaRequestId: data.loaRequestId,
                lOAFile: file,
            }).unwrap();

            if (res.success) {
                customFunction.updateState({
                    data: {
                        ...state.data,
                        ...res.returnData,
                    },
                });
            }

            refetch();
            customFunction.updateToggle("toggleAction");
            customFunction.updateState({
                tabRefreshKey: state?.tabRefreshKey + 1,
            });
            onUpdateParentKey(parentKey + 1);
        } else {
            customFunction.updateToggle("toggleAction", { ...findData, actionName: data });
        }
    };

    const BUTTONS = useMemo(
        () => [
            {
                label: "Return",
                icon: "ri-arrow-go-back-line",
                className: "emd-btn-warning",
                show: state?.data?.actions?.return,
                onClick: () => handleActions(false, "Return"),
            },
            {
                label: "Approve",
                icon: "ri-check-double-line",
                className: "emd-btn-success",
                show: state?.data?.actions?.approve,
                onClick: () => handleActions(false, "Approve"),
            },
            {
                label: "Reject",
                icon: "ri-thumb-down-line",
                className: "emd-btn-danger",
                show: state?.data?.actions?.reject,
                onClick: () => handleActions(false, "Reject"),
            },
            {
                label: "Cancel",
                icon: "ri-close-line",
                className: "emd-btn-danger",
                show: state?.data?.actions?.cancel,
                onClick: () => handleActions(false, "Cancel"),
            },
        ],
        [state?.data?.actions],
    );

    return (
        <React.Fragment>
            <ActionModal
                state={state}
                customFunction={customFunction}
                loaRequestId={findData?.loaRequestId}
                data={state?.trxValue}
                show={state.toggle.toggleAction}
                onCloseClick={handleActions}
            />

            <TransactionDetailsModal
                title={"Letter of Availment Request"}
                fullscreen={true}
                show={show}
                data={state.data}
                tabs={TABS}
                buttons={BUTTONS}
                onCloseClick={onCloseClick}
                tabRefreshKey={state.tabRefreshKey}
            />
        </React.Fragment>
    );
}
